import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour.css";

export type TourOutcome = "done" | "skipped";

/** driver.js popoverClass carrying the Mitigate skin (tour.css). */
export const TOUR_POPOVER_CLASS = "mitigate-tour-popover";

/**
 * Run a coach tour once and report how it ended, so the caller can record it.
 *
 * Shared by every tour in the app: the options here are the house style
 * (progress text, button labels, overlay colour) and the two behaviours that
 * matter for trust. `skipMissingElement` means an anchor that is not on the
 * page yet costs one step, not the whole tour; `disableActiveInteraction`
 * stays off so the tour never traps someone who just wants to get on with it.
 *
 * Returns a cleanup that tears the overlay down if the page unmounts mid-tour.
 */
export function runTour(
  steps: DriveStep[],
  onFinish: (outcome: TourOutcome) => void,
  popoverClass: string = TOUR_POPOVER_CLASS
): () => void {
  let settled = false;
  const finish = (outcome: TourOutcome) => {
    if (settled) return;
    settled = true;
    onFinish(outcome);
  };

  const tour = driver({
    steps,
    popoverClass,
    showProgress: true,
    progressText: "{{current}} of {{total}}",
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Got it",
    allowClose: true,
    overlayClickBehavior: "close",
    overlayColor: "#0b1119",
    overlayOpacity: 0.6,
    stagePadding: 6,
    stageRadius: 6,
    smoothScroll: true,
    skipMissingElement: true,
    disableActiveInteraction: false,
    onDestroyStarted: () => {
      // isLastStep is only meaningful before the state is torn down.
      finish(tour.isLastStep() ? "done" : "skipped");
      tour.destroy();
    },
  });

  tour.drive();

  return () => {
    // Unmounting mid-tour (route change) must not leave the overlay behind.
    if (tour.isActive()) tour.destroy();
    finish("skipped");
  };
}
